import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { medicationAPI } from '../../lib/api';
import { 
  Pill,
  MagnifyingGlass,
  Plus,
  Trash,
  Warning,
  CheckCircle,
  X,
  Upload,
  Sparkle,
  ArrowClockwise,
  ShieldCheck,
  FileText,
  CaretDown
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MedicationReconciliationPage() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Drug search
  const [drugSearch, setDrugSearch] = useState('');
  const [drugResults, setDrugResults] = useState([]);
  const [searchingDrugs, setSearchingDrugs] = useState(false);
  
  // Add medication form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMed, setNewMed] = useState({
    medication_name: '',
    strength: '',
    form: '',
    frequency: '',
    route: 'oral',
    prescriber: '',
    pharmacy: '',
    notes: ''
  });
  
  // Import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  
  // Interactions
  const [interactions, setInteractions] = useState(null);
  const [checkingInteractions, setCheckingInteractions] = useState(false);
  
  // AI Review
  const [aiReview, setAiReview] = useState(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);

  const checkInteractions = useCallback(async (meds) => {
    const activeMeds = meds.filter(m => m.is_active);
    if (activeMeds.length < 2) {
      setInteractions(null);
      return;
    }
    
    setCheckingInteractions(true);
    try {
      const medNames = activeMeds.map(m => m.medication_name);
      const response = await medicationAPI.checkInteractions(medNames);
      setInteractions(response.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error checking interactions:', error);
    } finally {
      setCheckingInteractions(false);
    }
  }, []);

  const fetchMedications = useCallback(async () => {
    if (!selectedPatient) return;
    setLoading(true);
    try {
      const response = await medicationAPI.getPatientMeds(selectedPatient.id, false);
      setMedications(response.data.medications || []);
      // Auto-check interactions when medications are loaded
      if (response.data.medications?.length > 1) {
        checkInteractions(response.data.medications);
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching medications:', error);
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  }, [selectedPatient, checkInteractions]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.patients || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchMedications();
    }
  }, [selectedPatient, fetchMedications]);

  const searchDrugs = async (query) => {
    if (query.length < 2) {
      setDrugResults([]);
      return;
    }
    setSearchingDrugs(true);
    try {
      const response = await medicationAPI.search(query);
      setDrugResults(response.data.medications || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching drugs:', error);
    } finally {
      setSearchingDrugs(false);
    }
  };

  const selectDrug = (drug) => {
    setNewMed({
      ...newMed,
      medication_name: drug.name,
      strength: drug.strength,
      form: drug.form,
      rxcui: drug.rxcui,
      drug_class: drug.class
    });
    setDrugSearch('');
    setDrugResults([]);
  };

  const addMedication = async () => {
    if (!selectedPatient || !newMed.medication_name) {
      toast.error('Please enter medication name');
      return;
    }
    
    try {
      await medicationAPI.add({
        patient_id: selectedPatient.id,
        ...newMed,
        source: 'manual'
      });
      toast.success('Medication added');
      setNewMed({
        medication_name: '',
        strength: '',
        form: '',
        frequency: '',
        route: 'oral',
        prescriber: '',
        pharmacy: '',
        notes: ''
      });
      setShowAddForm(false);
      fetchMedications();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const deleteMedication = async (medId) => {
    if (!confirm('Discontinue this medication?')) return;
    try {
      await medicationAPI.delete(medId);
      toast.success('Medication discontinued');
      fetchMedications();
    } catch (error) {
      toast.error('Failed to discontinue medication');
    }
  };

  const verifyMedication = async (medId) => {
    try {
      await medicationAPI.verify(medId);
      toast.success('Medication verified');
      fetchMedications();
    } catch (error) {
      toast.error('Failed to verify medication');
    }
  };

  const runAIReview = async () => {
    if (!selectedPatient) return;
    
    setAiReviewLoading(true);
    try {
      const response = await medicationAPI.aiReview(selectedPatient.id);
      setAiReview(response.data.review);
      toast.success('AI review complete');
    } catch (error) {
      toast.error('AI review failed');
    } finally {
      setAiReviewLoading(false);
    }
  };

  const importMedications = async () => {
    if (!selectedPatient || !importText.trim()) {
      toast.error('Please enter medication data');
      return;
    }
    
    try {
      // Parse the text - expecting format: "MedicationName, Strength, Frequency" per line
      const lines = importText.trim().split('\n');
      const meds = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          medication_name: parts[0] || '',
          strength: parts[1] || '',
          frequency: parts[2] || '',
          route: 'oral'
        };
      }).filter(m => m.medication_name);
      
      if (meds.length === 0) {
        toast.error('No valid medications found');
        return;
      }
      
      await medicationAPI.import({
        patient_id: selectedPatient.id,
        medications: meds
      });
      
      toast.success(`Imported ${meds.length} medications`);
      setShowImportModal(false);
      setImportText('');
      fetchMedications();
    } catch (error) {
      toast.error('Import failed');
    }
  };

  const filteredPatients = patients.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
    p.email.toLowerCase().includes(patientSearch.toLowerCase())
  );

  const activeMeds = medications.filter(m => m.is_active);
  const inactiveMeds = medications.filter(m => !m.is_active);

  return (
    <div className="p-8" data-testid="medication-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Medication Reconciliation
          </h1>
          <p className="text-slate-500">Review and manage patient medications</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Patient Selection & Med List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Selection */}
          <div className="card-clinical p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Patient</h2>
            <div className="relative mb-4">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Search patients..."
                className="input-clinical pl-10"
              />
            </div>
            
            {selectedPatient ? (
              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                  <p className="text-sm text-slate-500">{selectedPatient.email}</p>
                </div>
                <button onClick={() => { setSelectedPatient(null); setMedications([]); setInteractions(null); setAiReview(null); }} className="p-2 hover:bg-teal-100 rounded-lg">
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {filteredPatients.slice(0, 10).map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className="w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100 text-left transition-colors"
                  >
                    <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                    <p className="text-sm text-slate-500">{patient.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Medication List */}
          {selectedPatient && (
            <div className="card-clinical p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Active Medications ({activeMeds.length})</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="btn-outline flex items-center gap-2 text-sm"
                  >
                    <Upload size={16} />
                    Import
                  </button>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="btn-teal flex items-center gap-2 text-sm"
                  >
                    <Plus size={16} />
                    Add Medication
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-8 text-center">
                  <ArrowClockwise size={24} className="animate-spin mx-auto text-teal-500" />
                </div>
              ) : activeMeds.length > 0 ? (
                <div className="space-y-3">
                  {activeMeds.map((med) => (
                    <div key={med.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Pill size={18} className="text-teal-500" />
                            <span className="font-medium text-slate-900">{med.medication_name}</span>
                            {med.strength && <span className="text-slate-600">{med.strength}</span>}
                            {med.verified_at && (
                              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                <ShieldCheck size={12} /> Verified
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-slate-500 space-x-3">
                            {med.frequency && <span>{med.frequency}</span>}
                            {med.form && <span>• {med.form}</span>}
                            {med.route && <span>• {med.route}</span>}
                          </div>
                          {med.prescriber && (
                            <div className="mt-1 text-xs text-slate-400">Prescriber: {med.prescriber}</div>
                          )}
                          {med.pharmacy && (
                            <div className="text-xs text-slate-400">Pharmacy: {med.pharmacy}</div>
                          )}
                          <div className="text-xs text-slate-400 mt-1">
                            Source: {med.source === 'manual' ? 'Manual Entry' : med.source === 'import' ? 'Imported' : 'Patient Reported'}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {!med.verified_at && (
                            <button
                              onClick={() => verifyMedication(med.id)}
                              className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                              title="Verify"
                            >
                              <ShieldCheck size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteMedication(med.id)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Discontinue"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 bg-slate-50 rounded-xl text-center">
                  <Pill size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active medications</p>
                </div>
              )}

              {/* Inactive/Discontinued Medications */}
              {inactiveMeds.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Discontinued ({inactiveMeds.length})</h3>
                  <div className="space-y-2">
                    {inactiveMeds.map((med) => (
                      <div key={med.id} className="p-3 bg-slate-100 rounded-lg opacity-60">
                        <div className="flex items-center gap-2">
                          <Pill size={16} className="text-slate-400" />
                          <span className="text-slate-600 line-through">{med.medication_name} {med.strength}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Interactions & AI Review */}
        <div className="space-y-6">
          {/* Drug Interactions */}
          {selectedPatient && (
            <div className="card-clinical p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Drug Interactions</h2>
                <button
                  onClick={() => checkInteractions()}
                  disabled={checkingInteractions || activeMeds.length < 2}
                  className="text-sm text-teal-600 hover:text-teal-700"
                >
                  {checkingInteractions ? <ArrowClockwise size={16} className="animate-spin" /> : 'Recheck'}
                </button>
              </div>

              {interactions ? (
                interactions.count > 0 ? (
                  <div className="space-y-3">
                    {interactions.interactions.map((interaction) => (
                      <div
                        key={`interaction-${interaction.drugs?.join('-') || interaction.description?.substring(0, 20)}`}
                        className={`p-3 rounded-lg border ${
                          interaction.severity === 'high'
                            ? 'bg-red-50 border-red-200'
                            : interaction.severity === 'moderate'
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Warning size={16} className={
                            interaction.severity === 'high' ? 'text-red-500' :
                            interaction.severity === 'moderate' ? 'text-amber-500' : 'text-blue-500'
                          } />
                          <span className={`text-xs font-medium uppercase ${
                            interaction.severity === 'high' ? 'text-red-600' :
                            interaction.severity === 'moderate' ? 'text-amber-600' : 'text-blue-600'
                          }`}>
                            {interaction.severity} severity
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">
                          {interaction.drug1} + {interaction.drug2}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">{interaction.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                    <CheckCircle size={24} className="text-green-500 mx-auto mb-2" />
                    <p className="text-green-700 font-medium">No interactions found</p>
                  </div>
                )
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-slate-400 text-sm">
                    {activeMeds.length < 2 ? 'Add at least 2 medications to check interactions' : 'Click Recheck to analyze'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AI Review */}
          {selectedPatient && (
            <div className="card-clinical p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkle size={20} className="text-amber-500" />
                  <h2 className="text-lg font-semibold text-slate-900">AI Review</h2>
                </div>
                <button
                  onClick={runAIReview}
                  disabled={aiReviewLoading || activeMeds.length === 0}
                  className="btn-teal text-sm flex items-center gap-2"
                >
                  {aiReviewLoading ? (
                    <ArrowClockwise size={16} className="animate-spin" />
                  ) : (
                    <Sparkle size={16} />
                  )}
                  {aiReviewLoading ? 'Analyzing...' : 'Run AI Review'}
                </button>
              </div>

              {aiReview ? (
                <div className="space-y-4">
                  {aiReview.summary && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700">{aiReview.summary}</p>
                    </div>
                  )}
                  
                  {aiReview.alerts?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-red-600 mb-2">Alerts</h4>
                      {aiReview.alerts.map((alert, idx) => (
                        <div key={`review-alert-${idx}-${typeof alert === 'string' ? alert.substring(0, 15) : idx}`} className="p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700 mb-2">
                          {typeof alert === 'string' ? alert : JSON.stringify(alert)}
                        </div>
                      ))}
                    </div>
                  )}

                  {aiReview.recommendations?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Recommendations</h4>
                      <ul className="space-y-1">
                        {aiReview.recommendations.map((rec, idx) => (
                          <li key={`review-rec-${idx}-${typeof rec === 'string' ? rec.substring(0, 15) : idx}`} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle size={14} className="text-teal-500 mt-0.5 flex-shrink-0" />
                            {typeof rec === 'string' ? rec : JSON.stringify(rec)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-slate-400 text-sm">Click "Run AI Review" for comprehensive analysis</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Medication Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Add Medication</h3>
              <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Drug Search */}
              <div>
                <label className="label-clinical">Search Drug Database</label>
                <div className="relative">
                  <input
                    type="text"
                    value={drugSearch}
                    onChange={(e) => { setDrugSearch(e.target.value); searchDrugs(e.target.value); }}
                    placeholder="Type to search medications..."
                    className="input-clinical"
                  />
                  {searchingDrugs && (
                    <ArrowClockwise size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                </div>
                {drugResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                    {drugResults.map((drug) => (
                      <button
                        key={`drug-${drug.name}-${drug.strength || ''}-${drug.form || ''}`}
                        onClick={() => selectDrug(drug)}
                        className="w-full p-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <p className="font-medium text-slate-900">{drug.name} {drug.strength}</p>
                        <p className="text-xs text-slate-500">{drug.form} • {drug.class}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Medication Name *</label>
                  <input
                    type="text"
                    value={newMed.medication_name}
                    onChange={(e) => setNewMed({ ...newMed, medication_name: e.target.value })}
                    className="input-clinical"
                    placeholder="e.g., Lisinopril"
                  />
                </div>
                <div>
                  <label className="label-clinical">Strength</label>
                  <input
                    type="text"
                    value={newMed.strength}
                    onChange={(e) => setNewMed({ ...newMed, strength: e.target.value })}
                    className="input-clinical"
                    placeholder="e.g., 10mg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Form</label>
                  <select
                    value={newMed.form}
                    onChange={(e) => setNewMed({ ...newMed, form: e.target.value })}
                    className="input-clinical"
                  >
                    <option value="">Select...</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Capsule">Capsule</option>
                    <option value="Liquid">Liquid</option>
                    <option value="Injection">Injection</option>
                    <option value="Inhaler">Inhaler</option>
                    <option value="Topical">Topical</option>
                    <option value="Patch">Patch</option>
                    <option value="Drops">Drops</option>
                  </select>
                </div>
                <div>
                  <label className="label-clinical">Frequency</label>
                  <select
                    value={newMed.frequency}
                    onChange={(e) => setNewMed({ ...newMed, frequency: e.target.value })}
                    className="input-clinical"
                  >
                    <option value="">Select...</option>
                    <option value="Once daily">Once daily</option>
                    <option value="Twice daily">Twice daily</option>
                    <option value="Three times daily">Three times daily</option>
                    <option value="Four times daily">Four times daily</option>
                    <option value="Every 4 hours">Every 4 hours</option>
                    <option value="Every 6 hours">Every 6 hours</option>
                    <option value="Every 8 hours">Every 8 hours</option>
                    <option value="As needed">As needed (PRN)</option>
                    <option value="Weekly">Weekly</option>
                    <option value="At bedtime">At bedtime</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Prescriber</label>
                  <input
                    type="text"
                    value={newMed.prescriber}
                    onChange={(e) => setNewMed({ ...newMed, prescriber: e.target.value })}
                    className="input-clinical"
                    placeholder="Dr. Smith"
                  />
                </div>
                <div>
                  <label className="label-clinical">Pharmacy</label>
                  <input
                    type="text"
                    value={newMed.pharmacy}
                    onChange={(e) => setNewMed({ ...newMed, pharmacy: e.target.value })}
                    className="input-clinical"
                    placeholder="CVS, Walgreens..."
                  />
                </div>
              </div>

              <div>
                <label className="label-clinical">Notes</label>
                <textarea
                  value={newMed.notes}
                  onChange={(e) => setNewMed({ ...newMed, notes: e.target.value })}
                  className="input-clinical h-20"
                  placeholder="Additional notes..."
                />
              </div>

              <button onClick={addMedication} className="w-full btn-teal">
                Add Medication
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Import Medications</h3>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Paste medication list from pharmacy printout or other source. Format: one medication per line.
              </p>
              <p className="text-xs text-slate-500">
                Format: Medication Name, Strength, Frequency
              </p>
              
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Lisinopril, 10mg, Once daily&#10;Metformin, 500mg, Twice daily&#10;Atorvastatin, 20mg, Once daily"
                className="input-clinical h-48 font-mono text-sm"
              />

              <button onClick={importMedications} className="w-full btn-teal flex items-center justify-center gap-2">
                <Upload size={18} />
                Import Medications
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
